import React from 'react';
import { Box, Typography, Card, CardContent, Stack, Chip, Alert } from '@mui/material';
import { Schedule, CheckCircle, Error, Upload, Info } from '@mui/icons-material';
import { useAppSelector } from '../../../../../app/store';

const SummaryTab: React.FC = () => {
  const selectedFeeSchedule = useAppSelector((state) => state.contract.currentFeeSchedule);

  if (!selectedFeeSchedule) {
    return (
      <Alert severity="warning">
        No fee schedule selected.
      </Alert>
    );
  }

  const { feeSummary } = selectedFeeSchedule;

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'Not specified';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const getUploadStatusColor = () => {
    if (feeSummary.uploadInProgress) return 'warning';
    if (feeSummary.isLastUploadSuccess === true) return 'success';
    if (feeSummary.isLastUploadSuccess === false) return 'error';
    return 'default';
  };

  const getUploadStatusText = () => {
    if (feeSummary.uploadInProgress) return 'Upload in Progress';
    if (feeSummary.isLastUploadSuccess === true) return 'Upload Successful';
    if (feeSummary.isLastUploadSuccess === false) return 'Upload Failed';
    return 'No Upload Status';
  };

  const getUploadStatusIcon = () => {
    if (feeSummary.uploadInProgress) return <Upload />;
    if (feeSummary.isLastUploadSuccess === true) return <CheckCircle />;
    if (feeSummary.isLastUploadSuccess === false) return <Error />;
    return <Info />;
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, color: '#1e293b', fontWeight: 600 }}>
        Fee Schedule Summary
      </Typography>

      <Stack spacing={3}>
        {/* Upload Status Card */}
        <Card elevation={2}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Schedule color="primary" />
              Upload Status
            </Typography>
            
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">Current Status</Typography>
                <Chip 
                  label={getUploadStatusText()}
                  color={getUploadStatusColor()}
                  icon={getUploadStatusIcon()}
                  sx={{ mt: 0.5 }}
                />
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">Created Date</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatDate(feeSummary.createDate)}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">Last Updated</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatDate(feeSummary.lastUpdate)}
                  </Typography>
                </Box>
              </Stack>

              {/* Upload Statistics */}
              {(feeSummary.successful > 0 || feeSummary.failed > 0) && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" color="text.secondary">Upload Statistics</Typography>
                    <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                      <Chip 
                        label={`${feeSummary.total} Total`} 
                        size="small" 
                        variant="outlined" 
                      />
                      <Chip 
                        label={`${feeSummary.successful} Success`} 
                        size="small" 
                        color="success" 
                        variant="outlined" 
                      />
                      <Chip 
                        label={`${feeSummary.failed} Failed`} 
                        size="small" 
                        color="error" 
                        variant={feeSummary.failed > 0 ? "filled" : "outlined"}
                      />
                    </Stack>
                  </Box>
                </Stack>
              )}

              {feeSummary.errors && feeSummary.errors.length > 0 && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Upload Errors ({feeSummary.errors.length})
                  </Typography>
                  <Alert severity="error">
                    <Stack spacing={1}>
                      {feeSummary.errors.slice(0, 5).map((error, index) => (
                        <Typography key={index} variant="body2">
                          • {error}
                        </Typography>
                      ))}
                      {feeSummary.errors.length > 5 && (
                        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                          ... and {feeSummary.errors.length - 5} more errors
                        </Typography>
                      )}
                    </Stack>
                  </Alert>
                </Box>
              )}

              {feeSummary.uploadErrorMessage && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Error Details
                  </Typography>
                  <Alert severity="error">
                    {feeSummary.uploadErrorMessage}
                  </Alert>
                </Box>
              )}

              {feeSummary.submittedForDelete && (
                <Alert severity="warning">
                  This fee schedule has been submitted for deletion.
                </Alert>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};

export default SummaryTab;
